/**
 * Schemi ANAC — sezione riservata al Super Admin.
 *
 * Gli schemi hanno una **data di decorrenza**: gli Allegati 1, 2 e 3 della
 * delibera 495/2024, come modificata dalla 481/2025, sono obbligatori dal
 * 22/01/2026. Tenerli come configurazione, e non come codice, e' cio' che
 * permette di recepire le delibere annuali senza un rilascio applicativo.
 *
 * Se l'API non risponde, la pagina mostra i descrittori che il back-office
 * conosce localmente, dichiarandolo: un elenco vuoto sarebbe piu' fuorviante di
 * un elenco dichiaratamente provvisorio.
 */

import { useCallback, useEffect } from "react";
import { elencaSchemi } from "../api/client";
import type { SchemaAnac } from "../api/types";
import { Avviso, Chip, Scheda, Scheletro } from "../components/primitive";
import { campiDelloSchema, SCHEMI_DI_RISERVA, SCHEMI_ULTERIORI } from "../lib/schemi-anac";
import { useRisorsa } from "../lib/useRisorsa";
import { IntestazionePagina } from "../layout/Guscio";

const SCHEMI_LOCALI: readonly SchemaAnac[] = [...SCHEMI_DI_RISERVA, ...SCHEMI_ULTERIORI];

export function SchemiAnac() {
  const carica = useCallback((segnale: AbortSignal) => elencaSchemi(segnale), []);
  const { dato, inCaricamento, errore } = useRisorsa(carica, []);

  useEffect(() => {
    document.title = "Schemi ANAC · Amministrazione trasparente";
  }, []);

  const dalServer = dato ?? [];
  const schemi = dalServer.length > 0 ? dalServer : SCHEMI_LOCALI;
  const locali = dalServer.length === 0;

  return (
    <div className="pila">
      <IntestazionePagina
        sopratitolo="Amministrazione"
        titolo="Schemi ANAC"
        sottotitolo="Tracciati di pubblicazione con la relativa data di decorrenza, per recepire le delibere annuali senza rilasci applicativi."
      />

      {errore ? (
        <Avviso tono="attenzione" titolo="Schemi non letti dall'API">
          {errore.messaggioEsteso}
        </Avviso>
      ) : null}

      {locali && !inCaricamento ? (
        <Avviso tono="attenzione" titolo="Elenco provvisorio">
          L'API non ha restituito schemi configurati: sono mostrati i descrittori noti al
          back-office. Vanno riconciliati con le specifiche tecniche pubblicate da ANAC prima del
          rilascio.
        </Avviso>
      ) : null}

      <Avviso tono="primario" titolo="Minimizzazione dei dati">
        La delibera 481/2025 ha rimosso dallo schema dell'art. 4 il codice fiscale del beneficiario,
        sostituito dalla sola tipologia di beneficiario a opzione vincolata. Il campo non e' nascosto
        dalla vista: non esiste nei tracciati.
      </Avviso>

      {inCaricamento ? (
        <Scheda titolo="Schemi">
          <Scheletro righe={6} />
        </Scheda>
      ) : (
        <div className="pila">
          {schemi.map((schema) => (
            <SchedaSchema key={schema.id_schema} schema={schema} />
          ))}
        </div>
      )}
    </div>
  );
}

function SchedaSchema({ schema }: { schema: SchemaAnac }) {
  const campi = campiDelloSchema(schema);

  return (
    <Scheda
      titolo={schema.denominazione}
      descrizione={`${schema.riferimento_normativo} · ${schema.delibera}`}
      azioni={
        <>
          <Chip tono={schema.attivo ? "positivo" : "neutro"}>
            {schema.attivo ? "Attivo" : "Non ancora attivo"}
          </Chip>
          <Chip tono="neutro" pallino={false}>
            dal {schema.decorrenza}
          </Chip>
        </>
      }
      fitto
    >
      {campi.length === 0 ? (
        <p className="campo__aiuto" style={{ padding: "1.25rem" }}>
          Tracciato non ancora definito nel sistema: finche' i campi non sono configurati, i
          contenuti di questo schema non si possono compilare nel back-office e arrivano
          dall'API o dal canale di ingresso.
        </p>
      ) : (
        <div className="tabella-guscio">
          <table className="tabella">
            <caption>
              {campi.length} campi. Il tipo determina il formato in cui il valore va inviato e
              la maschera con cui si mostra.
            </caption>
            <thead>
              <tr>
                <th scope="col">Campo</th>
                <th scope="col">Identificativo</th>
                <th scope="col">Tipo</th>
                <th scope="col">Obbligatorio</th>
                <th scope="col">Note</th>
              </tr>
            </thead>
            <tbody>
              {campi.map((campo) => (
                <tr key={campo.nome}>
                  <td className="tabella__principale">{campo.etichetta}</td>
                  <td className="dato testo-tenue">{campo.nome}</td>
                  <td>
                    <Chip tono="neutro" pallino={false}>
                      {campo.tipo}
                    </Chip>
                  </td>
                  <td>{campo.obbligatorio ? "Si'" : "No"}</td>
                  <td className="testo-tenue">
                    {campo.opzioni
                      ? `Opzioni vincolate: ${campo.opzioni.join(", ")}`
                      : (campo.aiuto ?? "—")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Scheda>
  );
}
